<template>
    <b-tabs card pills :nav-wrapper-class="{ 'd-none': hideTabs }" v-model="activeTab">
        <b-tab title="Task Details">
            <b-card>
                <b-form-group>
                    <template v-if="!value.id && templates.length && !value.title">
                        <a variant="link" href="#" @click.prevent="" id="createFromTemplate" class="pull-right">
                            Create from Template...
                        </a>
                        <b-popover :show.sync="showTemplateSelectionPopover" target="createFromTemplate"
                            triggers="click blur" placement="bottom" custom-class="task-template-popover">
                            <b-input v-model="filterTemplates" type="search" placeholder="Filter Templates"
                                class="mb-2" />

                            <div class="scroller">
                                <b-list-group>
                                    <b-list-group-item v-for="t in filteredTemplates" :key="t.id" href="#"
                                        @click="applyTemplate(t)">
                                        {{ t.title }}
                                    </b-list-group-item>
                                </b-list-group>
                            </div>
                        </b-popover>
                    </template>
                    <label>Title*</label>
                    <b-form-input :value="value.title" :readonly="fieldsDisabled || disableAdHocFields"
                        @input="$emit('input', { ...value, title: $event })" maxlength="300" />
                </b-form-group>

                <b-form-group label="Description">
                    <AutoCompleteTextarea :value="value.description" :readonly="fieldsDisabled || disableAdHocFields"
                        @input="$emit('input', { ...value, description: $event })" rows="6" class="textarea-autosize" />
                </b-form-group>

                <b-row>
                    <b-col v-if="!value.isScheduled && !value.isTemplate" lg="3">
                        <b-form-group label="Status">
                            <b-dropdown variant="link" size="sm" block class="status" no-caret :disabled="fieldsDisabled">
                                <template #button-content>
                                    <TaskStatusBadge :value="activeStatus" />
                                </template>

                                <b-dropdown-item @click="updateActiveStatus(UserTaskStatus.new)">
                                    <TaskStatusBadge :value="UserTaskStatus.new" />
                                </b-dropdown-item>
                                <b-dropdown-item @click="updateActiveStatus(UserTaskStatus.inProgress)">
                                    <TaskStatusBadge :value="UserTaskStatus.inProgress" />
                                </b-dropdown-item>
                                <b-dropdown-item v-if="value.id" @click="updateActiveStatus(UserTaskStatus.complete)">
                                    <TaskStatusBadge :value="UserTaskStatus.complete" />
                                </b-dropdown-item>
                            </b-dropdown>
                        </b-form-group>
                    </b-col>
                    <b-col :lg="!value.isScheduled && !value.isTemplate ? 3 : 4">
                        <b-form-group label="Store">
                            <SearchComponent :key="activeStore?.id" v-model="activeStore" label="" search-u-r-l="/Store"
                                :prepopulate="true" clearable :disabled="fieldsDisabled || disableAdHocFields" />
                        </b-form-group>
                    </b-col>
                    <b-col :lg="!value.isScheduled && !value.isTemplate ? 6 : 8">
                        <label>Assigned To{{ requireTaskAssignment ? '*' : '' }}</label>
                        <div class="pull-right d-flex" style="gap: 8px;">
                            <a v-if="!fieldsDisabled && !isAssignedToMe" href="#" @click.prevent="assignToMe">
                                <i class="far fa-user" />
                                Assign to Me
                            </a>
                            <a v-if="!!primaryPatient && !fieldsDisabled" href="#" @click.prevent="assignToPrimaryPatient">
                                <i class="fa fa-user" />
                                To Patient
                            </a>
                            <a v-if="!!primaryPrescriber && !fieldsDisabled" href="#" @click.prevent="assignToPrimaryPrescriber">
                                <i class="fa fa-user-md" />
                                To Prescriber
                            </a>
                        </div>
                        <b-form-group>
                            <multiselect v-model="activeAssignedTo" :options="assignToOptions" :multiple="allowMultipleAssignments"
                                group-values="options" group-label="type" label="name" track-by="id" :allow-empty="true"
                                :loading="loadingAssignToOptions" :disabled="fieldsDisabled" @search-change="assignToSearch">

                                <template #singleLabel="data">
                                    <p class="m-0" v-html="data.option.name"></p>
                                </template>

                                <template #option="data">
                                    <i v-if="data.option.$groupLabel === 'Teams'" class="fa fa-users"></i>
                                    <i v-if="data.option.$groupLabel === 'Users'" class="fa fa-user"></i>
                                    <i v-if="data.option.$groupLabel === 'Patients'" class="far fa-user"></i>
                                    <i v-if="data.option.$groupLabel === 'Prescribers'" class="fa fa-notes-medical"></i>
                                    <i v-if="data.option.$groupLabel === 'Brand Portal Users'" class="fa fa-globe"></i>
                                    {{ data.option.$groupLabel }}
                                    <p class="m-0" v-html="data.option.name"></p>
                                </template>

                                <template #tag="data">
                                    <b-badge v-if="data.option?.isTeam" variant="primary" class="mr-1 my-0">
                                        <i class="fa fa-users"></i>
                                        {{ data.option.displayName || data.option.name }}
                                        <i class="fa fa-close ml-1" @click="data.remove(data.option)"></i>
                                    </b-badge>
                                    <b-badge v-if="data.option?.isPatient" variant="warning" class="mr-1 my-0">
                                        <i class="far fa-user"></i>
                                        {{ data.option.displayName || data.option.name }}
                                        <i class="fa fa-close ml-1" @click="data.remove(data.option)"></i>
                                    </b-badge>
                                    <b-badge v-if="data.option?.isPrescriber" variant="danger" class="mr-1 my-0">
                                        <i class="fa fa-notes-medical"></i>
                                        {{ data.option.displayName || data.option.name }}
                                        <i class="fa fa-close ml-1" @click="data.remove(data.option)"></i>
                                    </b-badge>
                                    <b-badge v-if="data.option?.isUser" variant="info" class="mr-1 my-0">
                                        <i class="fa fa-user"></i>
                                        {{ data.option.displayName || data.option.name }}
                                        <i class="fa fa-close ml-1" @click="data.remove(data.option)"></i>
                                    </b-badge>
                                    <b-badge v-if="data.option?.isBrandPortalUser" variant="success" class="mr-1 my-0">
                                        <i class="fa fa-globe"></i>
                                        {{ data.option.displayName || data.option.name }}
                                        <i class="fa fa-close ml-1" @click="data.remove(data.option)"></i>
                                    </b-badge>
                                </template>
                            </multiselect>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row class="mb-2">
                    <b-col>
                        <label>
                            Watchers
                            <HelpPopup>
                                <template v-if="value.isScheduled">
                                    Watchers will automatically get added to each task created from this schedule.
                                </template>
                                <template v-else-if="value.isTemplate">
                                    Watchers will automatically get added to each task created from this template.
                                </template>
                                <template v-else>
                                    Watchers will get notified when this task completes.
                                </template>
                            </HelpPopup>
                        </label>
                        <a href="#"
                            v-if="!value.watchers?.find(w => w.userName?.toLowerCase() == $user.userName.toLowerCase()) && !fieldsDisabled"
                            @click.prevent="addMeAsWatcher" class="pull-right mt-1">
                            <i class="fa fa-eye" />
                            Add Me as Watcher
                        </a>
                        <b-form-group>
                            <multiselect v-model="activeWatchers" :options="watchersOptions" :multiple="true"
                                group-values="options" group-label="type" label="name" track-by="id" :disabled="fieldsDisabled">

                                <template #option="data">
                                    <i v-if="data.option.$groupLabel === 'Teams'" class="fa fa-users"></i>
                                    <i v-if="data.option.$groupLabel === 'Users'" class="fa fa-user"></i>
                                    {{ data.option.$groupLabel }}
                                    {{ data.option.name }}
                                </template>

                                <template #tag="data">
                                    <b-badge v-if="data.option.isTeam" variant="primary" class="mr-1 my-0">
                                        <i class="fa fa-users"></i>
                                        {{ data.option.name }}
                                        <i class="fa fa-close ml-1" @click="data.remove(data.option)"></i>
                                    </b-badge>
                                    <b-badge v-else variant="info" class="mr-1 my-0">
                                        <i class="fa fa-user"></i>
                                        {{ data.option.name }}
                                        <i class="fa fa-close ml-1" @click="data.remove(data.option)"></i>
                                    </b-badge>
                                </template>
                            </multiselect>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-check v-model="value.blockCheckout" switch :disabled="fieldsDisabled || disableAdHocFields">
                            Block Checkout
                            <HelpPopup>
                                <template v-if="value.isScheduled">
                                    When enabled, tasks created from this schedule will block checkout for any patients
                                    and/or prescriptions linked to the underlying task until it is completed or expired.
                                </template>
                                <template v-else-if="value.isTemplate">
                                    When enabled, tasks created from this template will block checkout for any patients
                                    and/or prescriptions linked to the underlying task until it is completed or expired.
                                </template>
                                <template v-else>
                                    When enabled, this task will block checkout for any linked patients and/or
                                    prescriptions until it is completed or expired.
                                </template>
                            </HelpPopup>
                        </b-check>
                    </b-col>
                </b-row>
            </b-card>

            <b-card v-if="!value.isScheduled && !value.isTemplate">
                <b-row cols-lg="2">
                    <b-col>
                        <b-form-group label="Start Date*">
                            <el-date-picker :value="startDatePickerValue" @input="handleStartDateChange" format="MM/dd/yyyy" type="date" :disabled="fieldsDisabled" />
                        </b-form-group>
                    </b-col>
                    <b-col>
                        <b-form-group>
                            <label>Due Date*</label>
                            <b-checkbox switch class="pull-right" :checked="value.dueAtTime"
                                :disabled="fieldsDisabled" @change="$emit('input', { ...value, dueAtTime: $event })">
                                Specify Due Time
                            </b-checkbox>
                            <b-row>
                                <b-col>
                                    <el-date-picker format="MM/dd/yyyy" type="date"
                                        :value="dueDatePickerValue" @input="handleDateChange" :disabled="fieldsDisabled" />
                                </b-col>
                                <b-col v-if="value.dueAtTime">
                                    <b-form-timepicker size="sm" minutes-step="15"
                                        :value="moment.utc(value.dueDate).local().format('HH:mm:ss')"
                                        :disabled="fieldsDisabled" @input="handleTimeChange" />
                                </b-col>
                            </b-row>
                        </b-form-group>
                    </b-col>
                    <b-col>
                        <b-form-group>
                            <label>Expiration Date</label>
                            <HelpPopup>
                                If task is not completed by expiration date, the task will transition to "Expired"
                                status.
                            </HelpPopup>
                            <el-date-picker format="MM/dd/yyyy" type="date" :value="expirationDatePickerValue" @input="handleExpiresDateChange" clearable :disabled="fieldsDisabled" />
                        </b-form-group>
                    </b-col>
                </b-row>
            </b-card>

            <template v-if="value.isScheduled">
                <b-card cols-lg="4" cols="12">
                    <b-row>
                        <b-col>
                            <b-form-group>
                                <b-checkbox switch v-model="value.enabled" :disabled="fieldsDisabled">
                                    Enabled
                                </b-checkbox>
                            </b-form-group>

                            <b-form-group label="Start Date*">
                                <el-date-picker :value="startDatePickerValue" @input="handleStartDateChange" format="MM/dd/yyyy" type="date" :disabled="fieldsDisabled" />
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group label="Due Date">
                                <div class="d-flex" style="gap: 6px;">
                                    <b-input style="width: 60px" min="0" :value="dueDateDaysAfter"
                                        :disabled="fieldsDisabled" @input="handleDueAfterCreation" type="number" />
                                    Day(s) After Creation
                                </div>
                            </b-form-group>
                            <b-checkbox switch :checked="value.dueAtTime"
                                :disabled="fieldsDisabled" @change="$emit('input', { ...value, dueAtTime: $event })">
                                Specify Due Time
                            </b-checkbox>
                            <b-form-timepicker v-if="value.dueAtTime" size="sm" minutes-step="15"
                                :value="moment.utc(value.dueDate).local().format('HH:mm:ss')" @input="handleTimeChange"
                                :disabled="fieldsDisabled" dropup />
                        </b-col>
                        <b-col>
                            <b-checkbox switch :checked="!!value.expiredOnDate"
                                @change="handleExpiresAfterCreation($event ? '1' : undefined)">
                                Expires
                            </b-checkbox>
                            <div v-if="expirationDaysAfter" class="d-flex" style="gap: 6px;">
                                <b-input style="width: 60px" min="0" :value="expirationDaysAfter"
                                    @input="handleExpiresAfterCreation" type="number" />
                                Day(s) After Creation
                            </div>
                            <b-form-group v-if="!!value.expiredOnDate" label="Follow-Up Template on Expiry" class="mt-2">
                                <TaskTemplateField :value="value.expiryFollowUpTemplateId"
                                    @input="$emit('input', { ...value, expiryFollowUpTemplateId: $event })" />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-card>

                <b-card>
                    <b-row>
                        <b-col cols="12" lg="4">
                            <b-form-group label="Schedule Type">
                                <b-select :value="value.repeatMode" :options="getScheduleTypeOptions"
                                    :disabled="fieldsDisabled" @input="$emit('input', { ...value, repeatMode: $event, repeatSchedule: '' })" />
                            </b-form-group>
                        </b-col>
                        <b-col cols="12" lg="8">
                            <b-form-group label="Schedule*">
                                <div v-if="value.repeatMode === 0" class="d-flex" style="gap: 6px;">
                                    Every <b-input type="number" style="width: 60px;" min="1"
                                        :value="value.repeatSchedule" :disabled="fieldsDisabled"
                                        @input="$emit('input', { ...value, repeatSchedule: $event })" />
                                    Day(s)
                                </div>
                                <div v-if="value.repeatMode === 1" class="d-flex flex-wrap" style="gap: 6px;">
                                    <b-checkbox
                                        v-for="(d, i) in ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']"
                                        switch size="sm" :key="d"
                                        :checked="value.repeatSchedule?.includes(i.toString())"
                                        :disabled="fieldsDisabled" @change="handleDaySchedule(i)">
                                        {{ d }}
                                    </b-checkbox>
                                </div>
                                <div v-if="value.repeatMode === 2">
                                    <div class="schedule-month-grid">
                                        <b-checkbox v-for="d in Array.from({ length: 31 }, (_, i) => i + 1)" switch
                                            size="sm" :key="d"
                                            :checked="value.repeatSchedule?.split(',').includes(d.toString())"
                                            @change="handleMonthSchedule(d)">
                                            {{ d > 28 ? d + "*" : d }}
                                        </b-checkbox>
                                    </div>

                                    <b-alert :show="value.repeatSchedule?.split(',').some(s => Number(s) > 28)"
                                        style="line-height: 1.3;" variant="info">
                                        <strong>
                                            *On months that have less than this many days, this task will be created on
                                            the last day of the month.
                                        </strong>
                                    </b-alert>
                                </div>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-card>
            </template>
            <template v-else-if="value.isTemplate">
                <b-card>
                    <b-row cols-lg="3" cols="1">
                        <b-col>
                            <b-form-group>
                                <b-checkbox switch v-model="value.enabled">
                                    Enabled
                                </b-checkbox>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group label="Due Date">
                                <div class="d-flex" style="gap: 6px;">
                                    <b-input style="width: 60px" min="0" :value="dueDateDaysAfter"
                                        :disabled="fieldsDisabled" @input="handleDueAfterCreation" type="number" />
                                    Day(s) After Creation
                                </div>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-checkbox switch :checked="!!value.expiredOnDate"
                                @change="handleExpiresAfterCreation($event ? '1' : undefined)">
                                Expires
                            </b-checkbox>
                            <div v-if="expirationDaysAfter" class="d-flex" style="gap: 6px;">
                                <b-input style="width: 60px" min="0" :value="expirationDaysAfter"
                                    @input="handleExpiresAfterCreation" type="number" />
                                Day(s) After Creation
                            </div>
                            <b-form-group v-if="!!value.expiredOnDate" label="Follow-Up Template on Expiry" class="mt-2">
                                <TaskTemplateField :value="value.expiryFollowUpTemplateId"
                                    @input="$emit('input', { ...value, expiryFollowUpTemplateId: $event })" />
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <!-- SD-3303: per-template cancellation-reason override -->
                    <b-row cols-lg="1" cols="1" class="mt-2">
                        <b-col>
                            <b-form-group label="Cancellation Reason Requirement">
                                <b-form-select :value="value.requireCancellationReasonOverride ?? null"
                                    :disabled="fieldsDisabled"
                                    @change="$emit('input', { ...value, requireCancellationReasonOverride: $event })"
                                    :options="[
                                        { text: 'Use global default', value: null },
                                        { text: 'Always require a reason', value: true },
                                        { text: 'Never require a reason', value: false },
                                    ]" />
                                <small class="text-muted">
                                    Overrides the global &quot;Require Reason When Cancelling Tasks&quot; configuration for tasks spawned from this template.
                                </small>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-card>

                <b-card>
                    <b-row cols-lg="3" cols="1">
                        <b-col>
                            <b-form-group>
                                <label>Limit to Teams</label>
                                <multiselect v-model="limitToTeams" :options="teams" :multiple="true" label="name"
                                    track-by="id" :disabled="fieldsDisabled" />
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group>
                                <label>Limit to Programs</label>
                                <multiselect v-model="limitToPrograms" :options="programs" :multiple="true" label="name"
                                    track-by="id" :disabled="fieldsDisabled" />
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group>
                                <label>Limit to Rx Statuses</label>
                                <multiselect v-model="limitToRxStatuses" :options="rxStatusOptions" :multiple="true"
                                    label="name" track-by="id" :disabled="fieldsDisabled" />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-card>

                <b-card>
                    <b-row cols-lg="1">
                        <b-col>
                            <b-checkbox switch v-model="value.autoOutboundEnabled" :disabled="fieldsDisabled">
                                Enable Auto Outbound
                                <HelpPopup>When enabled, patients linked to tasks created from this
                                template will receive automated outbound calls via Twilio Flow.</HelpPopup>
                            </b-checkbox>
                        </b-col>
                    </b-row>
                    <b-row cols-lg="3" v-if="value.autoOutboundEnabled" class="mt-3">
                        <b-col>
                            <b-form-group>
                                <label>Contact Delay (Hours)</label>
                                <HelpPopup>Minimum hours to wait between contact attempts for the same patient.</HelpPopup>
                                <b-input type="number" v-model.number="value.autoOutboundContactDelay" min="0"
                                    :disabled="fieldsDisabled" placeholder="0"
                                    @change="clampField('autoOutboundContactDelay', 0)" />
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group>
                                <label>Max Age (Days)</label>
                                <HelpPopup>Stop attempting outbound calls after this many days from task creation.</HelpPopup>
                                <b-input type="number" v-model.number="value.autoOutboundMaxAge" min="1"
                                    :disabled="fieldsDisabled" placeholder="7"
                                    @change="clampField('autoOutboundMaxAge', 1)" />
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group>
                                <label>Twilio Flow SID</label>
                                <HelpPopup>The Twilio Studio Flow SID to execute for outbound calls (starts with FW).</HelpPopup>
                                <b-input v-model="value.autoOutboundTwilioFlow" placeholder="FW..."
                                    :disabled="fieldsDisabled" />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-card>

                <b-card>
                    <b-row cols-lg="1">
                        <b-col>
                            <b-checkbox switch v-model="value.outreachEnabled" :disabled="fieldsDisabled">
                                Enable Auto Texts
                                <HelpPopup>When enabled, patients linked to tasks created from this template will automatically receive text messages until consent is captured or the max attempts are reached.</HelpPopup>
                            </b-checkbox>
                        </b-col>
                    </b-row>
                    <b-row v-if="value.outreachEnabled" class="mt-3">
                        <b-col cols="12">
                            <b-form-group>
                                <label>Text Message</label>
                                <HelpPopup>The message body sent to the patient for each outreach attempt.</HelpPopup>
                                <b-textarea ref="outreachTextarea" v-model="value.outreachTextTemplate" :disabled="fieldsDisabled"
                                    rows="3" placeholder="Enter the text message to send to the patient..." />
                                <div v-if="!fieldsDisabled" class="mt-1">
                                    <small class="text-muted mr-1">Available tokens:</small>
                                    <code v-for="token in outreachTokens" :key="token"
                                        class="outreach-token mr-1"
                                        @click="insertOutreachToken(token)">{{ '{' + token + '}' }}</code>
                                </div>
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <b-row cols-lg="2" v-if="value.outreachEnabled" class="mt-2">
                        <b-col>
                            <b-form-group>
                                <label>Contact Delay (Hours)</label>
                                <HelpPopup>Minimum hours to wait between text attempts for the same patient.</HelpPopup>
                                <b-input type="number" v-model.number="value.outreachContactDelay" min="0"
                                    :disabled="fieldsDisabled" placeholder="24"
                                    @change="clampField('outreachContactDelay', 0)" />
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group>
                                <label>Max Attempts</label>
                                <HelpPopup>Stop sending outreach texts after this many attempts with no response. If the task is linked to an acceptance record, that record is also marked Abandoned.</HelpPopup>
                                <b-input type="number" v-model.number="value.outreachMaxAttempts" min="1"
                                    :disabled="fieldsDisabled" placeholder="3"
                                    @change="clampField('outreachMaxAttempts', 1)" />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-card>
            </template>
            <template v-else>
                <b-row>
                    <b-col class="d-flex align-items-stretch">
                        <b-card>
                            <label>Linked Patients</label>
                            <b-table :items="activePatients"
                                :fields="[{ key: 'name', label: 'Patients' }, { key: 'action', label: '', class: 'action-col' }]"
                                foot-clone class="mb-0" thead-class="d-none">
                                <template #cell(name)="data">
                                    <LinkedPatientDetails :patient="data.item"
                                        :default-expanded="activePatients.length === 1">
                                        <span v-html="getPatientHtml(data.item)"></span>
                                    </LinkedPatientDetails>
                                </template>
                                <template #cell(action)="data">
                                    <b-button size="sm" variant="danger" :disabled="fieldsDisabled" @click="removePatient(data.item)">
                                        <i class="fa fa-trash" />
                                    </b-button>
                                </template>
                                <template #foot(name)="">
                                    <SearchComponent :key="activePatients.length" v-model="activePatient"
                                        :hide-label="true" search-u-r-l="/Patient" :prepopulate="false"
                                        :disabled="fieldsDisabled" :results-modifier="patientAddSearchResultFilter" />
                                </template>
                                <template #foot(action)="">
                                    <b-button size="sm" variant="success" disabled class="mb-0">
                                        <i class="fa fa-plus"></i>
                                    </b-button>
                                </template>
                            </b-table>
                        </b-card>
                    </b-col>
                    <b-col class="d-flex align-items-stretch">
                        <b-card>
                            <label>Linked Prescriptions</label>
                            <b-table :items="activePrescriptions"
                                :fields="[{ key: 'name', label: 'Prescriptions' }, { key: 'action', label: '', class: 'action-col' }]"
                                foot-clone class="mb-0" thead-class="d-none">
                                <template #cell(name)="data">
                                    <div v-html="getPrescriptionHtml(data.item)"></div>
                                </template>
                                <template #cell(action)="data">
                                    <b-button size="sm" variant="danger" :disabled="fieldsDisabled" @click="removePrescription(data.item)">
                                        <i class="fa fa-trash" />
                                    </b-button>
                                </template>
                                <template #foot(name)="">
                                    <SearchComponent :key="activePrescriptions.length" v-model="activePrescription"
                                        :hide-label="true" search-u-r-l="/Prescription" :prepopulate="false"
                                        :disabled="fieldsDisabled" :results-modifier="prescriptionAddSearchResultFilter"
                                        :option-label="prescriptionOptionLabel"/>
                                </template>
                                <template #foot(action)="">
                                    <b-button size="sm" variant="success" disabled class="mb-0">
                                        <i class="fa fa-plus"></i>
                                    </b-button>
                                </template>
                            </b-table>
                        </b-card>
                    </b-col>
                </b-row>
                <b-row v-if="eligibleDocumentPatientId">
                    <b-col>
                        <b-card>
                            <label>Linked Documents</label>
                            <b-table :items="activePatientDocuments"
                                :fields="[
                                    { key: 'fileName', label: 'File Name' },
                                    { key: 'tags', label: 'Tags' },
                                    { key: 'action', label: '', class: 'action-col' }
                                ]"
                                class="mb-2" thead-class="d-none" show-empty
                                empty-text="No documents linked.">
                                <template #cell(fileName)="data">
                                    <a href="#" @click.prevent="openDocument(data.item)">{{ data.item.fileName }}</a>
                                </template>
                                <template #cell(tags)="data">
                                    <b-badge v-for="tag in data.item.tags" :key="tag" variant="secondary" class="mr-1">
                                        {{ tag }}
                                    </b-badge>
                                </template>
                                <template #cell(action)="data">
                                    <b-button size="sm" variant="danger" :disabled="fieldsDisabled" @click="removeDocument(data.item)">
                                        <i class="fa fa-trash" />
                                    </b-button>
                                </template>
                            </b-table>
                            <b-button size="sm" variant="outline-primary" :disabled="fieldsDisabled" @click="openDocumentSelector">
                                <i class="fa fa-plus mr-1"></i>Add Documents...
                            </b-button>

                            <b-modal v-model="showDocumentSelector" title="Select Documents" size="lg" @ok="addDocuments" ok-title="Add Selected">
                                <div v-if="loadingDocuments" class="text-center py-3">
                                    <b-spinner small /> Loading documents...
                                </div>
                                <template v-else>
                                    <div v-if="documentsUniqueTags.length" class="mb-3">
                                        <b-badge v-for="tag in documentsUniqueTags" :key="tag"
                                            :variant="!documentFilterTags.includes(tag) ? 'secondary' : 'primary'"
                                            @click="toggleDocumentFilterTag(tag)" style="cursor: pointer;" class="mr-1">
                                            {{ tag }}
                                            <span class="count">{{ getDocumentCountForTag(tag) }}</span>
                                        </b-badge>
                                    </div>
                                    <b-table :items="filteredAvailableDocuments"
                                        :fields="[
                                            { key: 'select', label: '', class: 'action-col' },
                                            { key: 'fileName', label: 'File Name' },
                                            { key: 'tags', label: 'Tags' }
                                        ]"
                                        show-empty empty-text="No documents available.">
                                        <template #cell(select)="data">
                                            <b-checkbox v-model="data.item.select" />
                                        </template>
                                        <template #cell(fileName)="data">
                                            <a href="#" @click.prevent="openDocument(data.item)">{{ data.item.fileName }}</a>
                                        </template>
                                        <template #cell(tags)="data">
                                            <b-badge v-for="tag in data.item.tags" :key="tag" variant="secondary" class="mr-1">
                                                {{ tag }}
                                            </b-badge>
                                        </template>
                                    </b-table>
                                </template>
                            </b-modal>
                        </b-card>
                    </b-col>
                </b-row>

                <!--Dispensing Pharmacy-->
                <b-row>
                    <b-col>
                        <b-card>
                            <label>Dispensing Pharmacy</label>
                            <SearchComponent v-model="activeStoredPharmacy" label=""
                                search-u-r-l="/StoredPharmacy" :params="{ showOnlyActive: true }"
                                :prepopulate="true" clearable
                                :disabled="fieldsDisabled" />
                            <b-row v-if="value.storedPharmacy" class="mt-2">
                                <b-col cols="6" lg="3">
                                    <small class="text-muted">Name</small>
                                    <div>{{ value.storedPharmacy.name || '-' }}</div>
                                </b-col>
                                <b-col cols="6" lg="3">
                                    <small class="text-muted">Store #</small>
                                    <div>{{ value.storedPharmacy.storeNumber || '-' }}</div>
                                </b-col>
                                <b-col cols="6" lg="3">
                                    <small class="text-muted">Phone</small>
                                    <div>{{ value.storedPharmacy.phoneNumber || '-' }}</div>
                                </b-col>
                                <b-col cols="6" lg="3">
                                    <small class="text-muted">Fax</small>
                                    <div>{{ value.storedPharmacy.faxNumber || '-' }}</div>
                                </b-col>
                            </b-row>
                            <b-row v-if="value.storedPharmacy" class="mt-2">
                                <b-col cols="12" lg="6">
                                    <small class="text-muted">Address</small>
                                    <div>{{ storedPharmacyAddress }}</div>
                                </b-col>
                                <b-col cols="6" lg="3">
                                    <small class="text-muted">NPI</small>
                                    <div>{{ value.storedPharmacy.npiNumber || '-' }}</div>
                                </b-col>
                                <b-col cols="6" lg="3">
                                    <small class="text-muted">NCPDP</small>
                                    <div>{{ value.storedPharmacy.ncpdp || '-' }}</div>
                                </b-col>
                            </b-row>
                        </b-card>
                    </b-col>
                </b-row>

                <!--Ingestion Data-->
                <b-row v-if="value.ingestionData">
                    <b-col>
                        <b-card>
                            <div class="d-flex justify-content-between align-items-center">
                                <label class="mb-0">Ingestion Data</label>
                                <small class="text-muted">
                                    <i class="fa fa-clock"></i>
                                    Ingested: {{ value.ingestionData.created | formatDate("LLL") }}
                                </small>
                            </div>

                            <!--Prescription Information-->
                            <div class="mb-2">
                                <b-button v-b-toggle.ingestion-prescription variant="light" size="sm" block class="text-left border mb-0">
                                    <i class="fa fa-file-prescription"></i> Prescription Information
                                </b-button>
                                <b-collapse id="ingestion-prescription" visible>
                                    <div class="p-2 border border-top-0 rounded-bottom">
                                        <b-row>
                                            <b-col cols="6" lg="3">
                                                <small class="text-muted">Rx Number</small>
                                                <div>{{ value.ingestionData.rxNumber || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="2">
                                                <small class="text-muted">Store Number</small>
                                                <div>{{ value.ingestionData.storeNumber || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="2">
                                                <small class="text-muted">Fill Number</small>
                                                <div>{{ value.ingestionData.fillNumber || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="2">
                                                <small class="text-muted">Fill Status</small>
                                                <div>{{ value.ingestionData.fillStatus || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="3">
                                                <small class="text-muted">Plan Copay</small>
                                                <div>{{ value.ingestionData.planCopayAmount || '-' }}</div>
                                            </b-col>
                                        </b-row>
                                        <b-row class="mt-2">
                                            <b-col cols="6" lg="4">
                                                <small class="text-muted">Drug Name</small>
                                                <div>{{ value.ingestionData.drugName || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="4">
                                                <small class="text-muted">NDC</small>
                                                <div>{{ value.ingestionData.ndc || '-' }}</div>
                                            </b-col>
                                        </b-row>
                                    </div>
                                </b-collapse>
                            </div>

                            <!--Prescriber Information-->
                            <div class="mb-2">
                                <b-button v-b-toggle.ingestion-prescriber variant="light" size="sm" block class="text-left border mb-0">
                                    <i class="fa fa-user-md"></i> Prescriber Information
                                </b-button>
                                <b-collapse id="ingestion-prescriber">
                                    <div class="p-2 border border-top-0 rounded-bottom">
                                        <b-row>
                                            <b-col cols="6" lg="2">
                                                <small class="text-muted">ID</small>
                                                <div>{{ value.ingestionData.prescriberID || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="3">
                                                <small class="text-muted">First Name</small>
                                                <div>{{ value.ingestionData.prescriberFirstName || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="2">
                                                <small class="text-muted">Middle Name</small>
                                                <div>{{ value.ingestionData.prescriberMiddleName || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="3">
                                                <small class="text-muted">Last Name</small>
                                                <div>{{ value.ingestionData.prescriberLastName || '-' }}</div>
                                            </b-col>
                                        </b-row>
                                        <b-row class="mt-2">
                                            <b-col cols="12" lg="6">
                                                <small class="text-muted">Address</small>
                                                <div>{{ prescriberAddress }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="3">
                                                <small class="text-muted">Phone</small>
                                                <div>{{ value.ingestionData.prescriberPhone || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="3">
                                                <small class="text-muted">Fax</small>
                                                <div>{{ value.ingestionData.prescriberFax || '-' }}</div>
                                            </b-col>
                                        </b-row>
                                        <b-row class="mt-2">
                                            <b-col cols="6" lg="4">
                                                <small class="text-muted">NPI</small>
                                                <div>{{ value.ingestionData.prescriberNPI || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="4">
                                                <small class="text-muted">DEA</small>
                                                <div>{{ value.ingestionData.prescriberDEA || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="4">
                                                <small class="text-muted">State License</small>
                                                <div>{{ value.ingestionData.prescriberStateLicense || '-' }}</div>
                                            </b-col>
                                        </b-row>
                                    </div>
                                </b-collapse>
                            </div>

                            <!--Patient External IDs-->
                            <div class="mb-2">
                                <b-button v-b-toggle.ingestion-patient-ids variant="light" size="sm" block class="text-left border mb-0">
                                    <i class="fa fa-id-card"></i> Patient External IDs
                                </b-button>
                                <b-collapse id="ingestion-patient-ids">
                                    <div class="p-2 border border-top-0 rounded-bottom">
                                        <b-row>
                                            <b-col cols="6">
                                                <small class="text-muted">External Patient ID</small>
                                                <div>{{ value.ingestionData.patientID || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6">
                                                <small class="text-muted">ICD-10 Code</small>
                                                <div>{{ value.ingestionData.icd10Code || '-' }}</div>
                                            </b-col>
                                        </b-row>
                                    </div>
                                </b-collapse>
                            </div>

                            <!--Primary Insurance-->
                            <div class="mb-2">
                                <b-button v-b-toggle.ingestion-primary-ins variant="light" size="sm" block class="text-left border mb-0">
                                    <i class="fa fa-shield-alt"></i> Primary Insurance
                                </b-button>
                                <b-collapse id="ingestion-primary-ins">
                                    <div class="p-2 border border-top-0 rounded-bottom">
                                        <b-row>
                                            <b-col cols="6" lg="3">
                                                <small class="text-muted">Plan ID</small>
                                                <div>{{ value.ingestionData.primaryPlanID || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="3">
                                                <small class="text-muted">Plan Name</small>
                                                <div>{{ value.ingestionData.primaryPlanIDName || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="3">
                                                <small class="text-muted">Cardholder ID</small>
                                                <div>{{ value.ingestionData.cardHolderID || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="3">
                                                <small class="text-muted">Person Code</small>
                                                <div>{{ value.ingestionData.personCode || '-' }}</div>
                                            </b-col>
                                        </b-row>
                                        <b-row class="mt-2">
                                            <b-col cols="6" lg="3">
                                                <small class="text-muted">BIN</small>
                                                <div>{{ value.ingestionData.primaryPlanBIN || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="3">
                                                <small class="text-muted">PCN</small>
                                                <div>{{ value.ingestionData.primaryPlanPCN || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="3">
                                                <small class="text-muted">Group Number</small>
                                                <div>{{ value.ingestionData.primaryGroupNumber || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="3">
                                                <small class="text-muted">Copay</small>
                                                <div>{{ value.ingestionData.primaryCopayAmount || '-' }}</div>
                                            </b-col>
                                        </b-row>
                                        <b-row class="mt-2">
                                            <b-col cols="12" lg="6">
                                                <small class="text-muted">Address</small>
                                                <div>{{ primaryInsuranceAddress }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="6">
                                                <small class="text-muted">Phone</small>
                                                <div>{{ value.ingestionData.primaryPlanPhone || '-' }}</div>
                                            </b-col>
                                        </b-row>
                                    </div>
                                </b-collapse>
                            </div>

                            <!--Secondary Insurance-->
                            <div v-if="hasSecondaryInsurance" class="mb-2">
                                <b-button v-b-toggle.ingestion-secondary-ins variant="light" size="sm" block class="text-left border mb-0">
                                    <i class="fa fa-shield-alt"></i> Secondary Insurance
                                </b-button>
                                <b-collapse id="ingestion-secondary-ins">
                                    <div class="p-2 border border-top-0 rounded-bottom">
                                        <b-row>
                                            <b-col cols="6" lg="3">
                                                <small class="text-muted">Plan ID</small>
                                                <div>{{ value.ingestionData.secondaryPlanID || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="3">
                                                <small class="text-muted">Plan Name</small>
                                                <div>{{ value.ingestionData.secondaryPlanIDName || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="3">
                                                <small class="text-muted">BIN</small>
                                                <div>{{ value.ingestionData.secondaryPlanBIN || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="3">
                                                <small class="text-muted">PCN</small>
                                                <div>{{ value.ingestionData.secondaryPlanPCN || '-' }}</div>
                                            </b-col>
                                        </b-row>
                                        <b-row class="mt-2">
                                            <b-col cols="6" lg="3">
                                                <small class="text-muted">Group Number</small>
                                                <div>{{ value.ingestionData.secondaryGroupNumber || '-' }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="3">
                                                <small class="text-muted">Copay</small>
                                                <div>{{ value.ingestionData.secondaryCopayAmount || '-' }}</div>
                                            </b-col>
                                            <b-col cols="12" lg="6">
                                                <small class="text-muted">Address</small>
                                                <div>{{ secondaryInsuranceAddress }}</div>
                                            </b-col>
                                            <b-col cols="6" lg="6">
                                                <small class="text-muted">Phone</small>
                                                <div>{{ value.ingestionData.secondaryPlanPhone || '-' }}</div>
                                            </b-col>
                                        </b-row>
                                    </div>
                                </b-collapse>
                            </div>

                            <!--Fill History & Details-->
                            <div v-if="value.ingestionData.refillHistory || value.ingestionData.prescriptionInformation" class="mb-2">
                                <b-button v-b-toggle.ingestion-fill-history variant="light" size="sm" block class="text-left border mb-0">
                                    <i class="fa fa-history"></i> Fill History &amp; Details
                                </b-button>
                                <b-collapse id="ingestion-fill-history">
                                    <div class="p-2 border border-top-0 rounded-bottom">
                                        <b-row v-if="value.ingestionData.refillHistory">
                                            <b-col>
                                                <small class="text-muted">Refill History</small>
                                                <pre class="mb-0 mt-1" style="white-space: pre-wrap; font-size: 0.85em;">{{ value.ingestionData.refillHistory }}</pre>
                                            </b-col>
                                        </b-row>
                                        <b-row v-if="value.ingestionData.prescriptionInformation" class="mt-2">
                                            <b-col>
                                                <small class="text-muted">Prescription Information</small>
                                                <pre class="mb-0 mt-1" style="white-space: pre-wrap; font-size: 0.85em;">{{ value.ingestionData.prescriptionInformation }}</pre>
                                            </b-col>
                                        </b-row>
                                    </div>
                                </b-collapse>
                            </div>

                            <!--Additional Fields-->
                            <div v-if="parsedAdditionalFields && parsedAdditionalFields.length" class="mb-2">
                                <b-button v-b-toggle.ingestion-additional variant="light" size="sm" block class="text-left border mb-0">
                                    <i class="fa fa-plus-square"></i> Additional Fields
                                </b-button>
                                <b-collapse id="ingestion-additional">
                                    <div class="p-2 border border-top-0 rounded-bottom">
                                        <b-table :items="parsedAdditionalFields" :fields="['field', 'value']"
                                            small striped class="mb-0" />
                                    </div>
                                </b-collapse>
                            </div>
                        </b-card>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col>
                        <b-card v-if="value.id">
                            <NotesForm ref="notesForm" :entity-id="value.id" :entity-type="EntityType.UserTask"
                                save-button-label="Add Note" text-box-label="Note" @saved="refreshNotes" />
                            <NotesList ref="notesList" :entity-id="value.id" :entity-type="EntityType.UserTask"
                                :page-size="5" />
                        </b-card>
                    </b-col>
                </b-row>
                <!-- <b-row>
                    <b-col>
                        Editor value:
                        {{ value }}
                    </b-col>
                </b-row> -->
            </template>

            <b-card v-if="isNewTask || value.isScheduled || value.isTemplate || activeCompletionForm?.id">
                Task Completion Form
                <SearchComponent v-model="activeCompletionForm" :hide-label="true" :clearable="true" :prepopulate="true"
                    search-u-r-l="/Task/completion-form" :params="{ onlyActive: true }"
                    :disabled="!(isNewTask || value.isScheduled || value.isTemplate)" />
            </b-card>
        </b-tab>
        <b-tab v-if="showQuestionsTab" title="Completion Questions">
            <TaskQuestions :questions="value.completionQuestionSet?.questions"
                @input="handleQuestionsUpdate"
                :disabled="fieldsDisabled || disableAdHocFields || (!(value.isScheduled || value.isTemplate || !value.id))"
                :editing="(!isRegularTask || isNewTask)" :answers="value.completionAnswers"
                :show-errors="showCompletionQuestionErrors" />
        </b-tab>
        <b-tab v-if="value.completionFormImageId" title="Completion Form">
            <div style="position: relative;">
                <ImageComponent style="height:calc(100vh - 300px);" :imageID="value.completionFormImageId" />

                <div class="w-100 text-center" style="position: absolute; top: -22px; font-size: 1.9em;">
                    <div class="d-inline-block rounded">
                        <b-badge v-if="value.completionFormImageAccepted === true" variant="success" class="px-4"
                            :id="value.signatureData ? 'accepted-badge-' + value.id : null"
                            :style="value.signatureData ? 'cursor: pointer;' : null">
                            <div class="my-2">
                                Accepted
                            </div>
                            <div v-if="value.signatureData"
                                style="font-size: 0.7em; font-weight: 600; text-decoration: underline;" class="mb-1">
                                <i class="fa fa-signature mr-1"></i>View Signature...
                            </div>
                        </b-badge>
                        <b-badge v-if="value.completionFormImageAccepted === false" variant="danger" class="px-4">
                            Rejected
                        </b-badge>
                    </div>
                </div>

                <!-- Signature Popover -->
                <b-popover v-if="value.signatureData && value.completionFormImageAccepted === true"
                    :target="'accepted-badge-' + value.id" triggers="click" placement="bottom"
                    title="Electronic Signature">
                    <div class="signature-popover-content">
                        <div class="mb-3">
                            <img :src="value.signatureData"
                                style="max-width: 200px; height: auto; border: 1px solid #dee2e6; border-radius: 4px;"
                                alt="Electronic Signature" />
                        </div>
                        <div class="text-center">
                            <strong>Date Signed:</strong><br>
                            <span>{{ value.signatureDate | formatDate("L") }}</span>
                        </div>
                    </div>
                </b-popover>
            </div>

        </b-tab>
    </b-tabs>
</template>

<script lang="ts">
import { DatePicker } from "element-ui";
import Multiselect from 'vue-multiselect';
import { Component, Vue, Watch, Ref, Prop } from "vue-property-decorator";
import SearchComponent from "@/components/SearchComponent.vue";
import { UserTask, UserTaskStatus, UserTaskAnswer, UserTaskQuestion, BrandPortalUser } from "@/models/UserTask";
import { PatientDocument } from "@/models/PatientDocument";
import { Store } from "@/models/Store";
import { PharmacyRecord } from "@/models/Pharmacy";
import { Patient } from "@/models/Patient";
import { Prescription, RefillStatusCode } from "@/models/Prescription";
import { Team } from "@/models/Team";
import { User } from "@/models/User";
import TaskStatusBadge from "./TaskStatusBadge.vue";
import { EntityType } from "@/models/EntityType";
import NotesList from "../Note/NotesList.vue";
import NotesForm from "../Note/NotesForm.vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import TextUtils from "@/util/TextUtils";
import moment from "moment";
import HelpPopup from "../HelpPopup.vue";
import { Prescriber } from "@/models/Prescriber";
import { debounce } from "throttle-debounce";
import Axios, { CancelTokenSource } from "axios";
import TaskQuestions from "./TaskQuestions.vue";
import TaskTemplateField from "../Configuration/TaskTemplateField.vue";
import { Program } from "@/models/Program";
import { BPopover } from "bootstrap-vue";
import { CustomFaxTemplateBase } from "@/models/FaxTemplates";
import ImageComponent from "../Prescription/ImageComponent.vue";
import AutoCompleteTextarea from '@/components/AutoCompleteTextarea.vue';
import LinkedPatientDetails from "./LinkedPatientDetails.vue";

@Component({
    components: {
        AutoCompleteTextarea,
        SearchComponent,
        [DatePicker.name]: DatePicker,
        Multiselect,
        TaskStatusBadge,
        NotesForm,
        NotesList,
        HelpPopup,
        TaskQuestions,
        TaskTemplateField,
        ImageComponent,
        LinkedPatientDetails
    }
})
export default class TaskEditor extends Vue {

    @Prop({ required: true }) protected value!: UserTask;
    @Prop({ default: false }) protected showCompletionQuestionErrors!: boolean;
    @Prop({ default: false }) protected fieldsDisabled!: boolean;

    @Ref("notesList") protected notesList!: any;
    @Ref("notesForm") protected notesForm!: any;

    protected teams: Team[] = [];
    protected users: User[] = [];
    protected templates: UserTask[] = [];
    protected programs: Program[] = [];
    protected brandPortalUsers: BrandPortalUser[] = [];

    private activeAssignedTo: any = [];
    private activeWatchers: any = [];
    private activeStore = new Store();
    private activeStoredPharmacy: PharmacyRecord = new PharmacyRecord();
    private activePatients: Patient[] = [];
    private activePrescriptions: Prescription[] = [];
    private activePatient = new Patient();
    private activePrescription = new Prescription();
    private activeStatus: UserTaskStatus | null = UserTaskStatus.new;
    private activeCompletionForm: CustomFaxTemplateBase = new CustomFaxTemplateBase();

    private patientOptions: Patient[] = [];
    private loadingPatientOptions = false;

    private prescriberOptions: Prescriber[] = [];
    private loadingPrescriberOptions = false;

    protected activeAnswers: UserTaskAnswer[] = [];

    private showDocumentSelector = false;
    private availableDocuments: PatientDocument[] = [];
    private documentFilterTags: string[] = [];
    private loadingDocuments = false;

    protected filterTemplates = "";

    protected activeTab = 0;

    private allowMultipleAssignments = false;
    public requireTaskAssignment = false;

    UserTaskStatus = UserTaskStatus;
    EntityType = EntityType;

    moment = moment;

    async mounted() {
        // Load configuration FIRST before setting up the task
        await this.loadConfiguration();

        if (!this.value.id) this.getTemplates();
        this.getTeams();
        this.getUsers();
        this.getBrandPortalUsers();
        if (this.value.isTemplate) {
            this.getPrograms();
        }

        this.setupTask();
    }

    async loadConfiguration() {
        try {
            const response = await this.$http.post<Record<string, boolean>>("/Configuration/values", ["AllowMultipleTaskAssignments", "RequireTaskAssignment"]);
            this.allowMultipleAssignments = response.data.AllowMultipleTaskAssignments ?? false;
            this.requireTaskAssignment = !this.value.isTemplate && (response.data.RequireTaskAssignment ?? false);
        } catch (err) {
            console.error("Failed to load task configuration:", err);
            this.allowMultipleAssignments = false;
            this.requireTaskAssignment = false;
        }
    }

    setupTask() {
        this.activeStore = Object.assign(new Store(), { id: this.value.storeId });
        this.activeStoredPharmacy = this.value.storedPharmacy
            ? Object.assign(new PharmacyRecord(), this.value.storedPharmacy)
            : new PharmacyRecord();
        this.activePrescriptions = this.value.prescriptions?.map(p => Object.assign(new Prescription(), p)) ?? [];
        this.activePatients = this.value.patients?.map(p => Object.assign(new Patient(), p)) ?? [];
        const assignments = this.value.assignments?.map(p => ({
            id: p.brandPortalUserEmail ? `brandportal-${p.brandPortalUserEmail}` : p.patientId ? `patient-${p.patientId}` : p.prescriberId ? `prescriber-${p.prescriberId}` : p.teamId ? p.teamId : p.userName,
            name: p.brandPortalUserEmail ? p.brandPortalUserEmail : p.patientId ? Object.assign(new Patient(), p.patient).toString() : p.prescriberId ? Object.assign(new Prescriber(), p.prescriber).toString() : p.teamId ? p.teamName : p.userName,
            displayName: p.patientId ? Object.assign(new Patient(), p.patient).displayNamesForPerson() : p.prescriberId ? Object.assign(new Prescriber(), p.prescriber).displayNamesForPerson() : undefined,
            email: p.brandPortalUserEmail,
            isUser: !p.teamId && !p.patientId && !p.prescriberId && !p.brandPortalUserEmail,
            isTeam: !!p.teamId,
            isPatient: !!p.patientId,
            isPrescriber: !!p.prescriberId,
            isBrandPortalUser: !!p.brandPortalUserEmail
        })) ?? [];

        // Handle assignment display based on configuration and existing data
        // IMPORTANT: If task already has multiple assignments, preserve them even if feature is disabled
        // This prevents data loss when editing existing tasks with multiple assignments
        if (this.allowMultipleAssignments || assignments.length <= 1) {
            // Normal case: feature enabled, or only 0-1 assignments exist
            this.activeAssignedTo = this.allowMultipleAssignments ? assignments : (assignments.length > 0 ? assignments[0] : null);
        } else {
            // Special case: feature disabled but task has multiple assignments
            // Keep as array to preserve data, multiselect will still render correctly
            this.activeAssignedTo = assignments;
            // Temporarily allow multiple for this task to prevent data loss
            this.allowMultipleAssignments = true;
        }

        this.activeWatchers = this.value.watchers?.map(p => ({
            id: p.teamId ? p.teamId : p.userName,
            name: p.teamId ? p.teamName : p.userName,
            isTeam: !!p.teamId
        })) ?? [];
        this.activeStatus = this.value.status ?? UserTaskStatus.new;
        this.activeAnswers = this.value.completionAnswers ?? [];
        this.onActiveAssignedToChange();
        this.activeCompletionForm = this.value.taskCompletionForm ?
            Object.assign(new CustomFaxTemplateBase(), this.value.taskCompletionForm) : new CustomFaxTemplateBase();
    }

    get primaryPatient() {
        if (this.value.patients?.length !== 1) return null;
        return this.value.patients[0];
    }

    get primaryPrescriber() {
        if (this.value.prescriptions?.length !== 1) return null;
        return this.value.prescriptions[0].prescriber;
    }

    get storedPharmacyAddress(): string {
        const p = this.value.storedPharmacy;
        if (!p) return '-';
        return [p.address, p.addressCity, p.addressState, p.addressZip].filter(Boolean).join(', ') || '-';
    }

    get prescriberAddress(): string {
        const d = this.value.ingestionData;
        if (!d) return '-';
        const line1 = [d.prescriberAddress1, d.prescriberAddress2].filter(Boolean).join(', ');
        const line2 = [d.prescriberCity, d.prescriberState, d.prescriberZip].filter(Boolean).join(', ');
        return [line1, line2].filter(Boolean).join(', ') || '-';
    }

    get primaryInsuranceAddress(): string {
        const d = this.value.ingestionData;
        if (!d) return '-';
        return [d.primaryPlanAddress, d.primaryPlanCity, d.primaryPlanState, d.primaryPlanZip].filter(Boolean).join(', ') || '-';
    }

    get secondaryInsuranceAddress(): string {
        const d = this.value.ingestionData;
        if (!d) return '-';
        return [d.secondaryPlanAddress, d.secondaryPlanCity, d.secondaryPlanState, d.secondaryPlanZip].filter(Boolean).join(', ') || '-';
    }

    get hasSecondaryInsurance(): boolean {
        const d = this.value.ingestionData;
        if (!d) return false;
        return !!(d.secondaryPlanID || d.secondaryPlanIDName || d.secondaryPlanBIN || d.secondaryPlanPCN);
    }

    get parsedAdditionalFields(): Array<{ field: string; value: string }> {
        const raw = this.value.ingestionData?.additionalFields;
        if (!raw) return [];
        try {
            const parsed = JSON.parse(raw);
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];
            return Object.entries(parsed)
                .filter(([, v]) => v)
                .map(([k, v]) => ({ field: k, value: v as string }))
                .sort((a, b) => a.field.localeCompare(b.field));
        } catch {
            return [];
        }
    }

    get isAssignedToMe(): boolean {
        const userName = this.$user?.userName;
        if (!userName) return false;

        if (Array.isArray(this.activeAssignedTo)) {
            return this.activeAssignedTo.some((a: any) => a.id === userName && a.isUser);
        } else if (this.activeAssignedTo) {
            return this.activeAssignedTo.id === userName && this.activeAssignedTo.isUser;
        }

        return false;
    }

    get limitToTeams() {
        return this.value.limitToTeamIds?.map(tid => this.teams.find(t => t.id === tid));
    }

    set limitToTeams(value) {
        if (!value) {
            this.value.limitToTeamIds = undefined;
        } else {
            this.value.limitToTeamIds = value!.map(i => i!.id);
        }
    }

    get limitToPrograms() {
        return this.value.limitToProgramIds?.map(pid => this.programs.find(p => p.id === pid));
    }

    set limitToPrograms(value) {
        if (!value) {
            this.value.limitToProgramIds = undefined;
        } else {
            this.value.limitToProgramIds = value!.map(i => i!.id);
        }
    }

    get limitToRxStatuses() {
        return this.value.limitToRxStatuses?.map(pid => this.rxStatusOptions.find(p => p.id === pid));
    }

    set limitToRxStatuses(value) {
        if (!value) {
            this.value.limitToRxStatuses = undefined;
        } else {
            this.value.limitToRxStatuses = value!.map(i => i!.id as RefillStatusCode);
        }
    }

    @Watch("value.status")
    handleStatusChange() {
        this.activeStatus = this.value.status;
    }

    @Watch("activeCompletionForm")
    handleCompletionFormChange() {
        this.$emit('input', {
            ...this.value,
            taskCompletionFormId: this.activeCompletionForm?.id,
            taskCompletionForm: this.activeCompletionForm
        });
    }

    @Watch("showCompletionQuestionErrors")
    handleShowCompletionQuestionErrorsChange() {
        if (this.showCompletionQuestionErrors) this.activeTab = 1;
    }

    async getTeams() {
        try {
            const response = await this.$http.get<Team[]>("/user/teams");
            this.teams = response.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    async getUsers() {
        try {
            const response = await this.$http.get<User[]>("/user");
            this.users = response.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    protected showTemplateSelectionPopover = false;

    async getTemplates() {
        try {
            const response = await this.$http.get<UserTask[]>("/task/templates");
            this.templates = response.data;

            if (this.isNewTask && this.disableAdHocFields) {
                this.showTemplateSelectionPopover = true;
            }
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    async getPrograms() {
        try {
            const response = await this.$http.get<Program[]>("/program");
            this.programs = response.data;
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    async getBrandPortalUsers() {
        try {
            const response = await this.$http.get<BrandPortalUser[]>("/user/brand-portal");
            this.brandPortalUsers = response.data;
        } catch (err) {
            console.error("Failed to load Brand Portal users:", err);
            // Don't show notification - this is optional functionality
        }
    }

    get rxStatusOptions() {
        return Object.entries(RefillStatusCode)
            .filter(([key]) => isNaN(Number(key)))
            .map(([name, id]) => ({ name, id }));
    }

    get assignToOptions() {

        const defaultOptions = [
            {
                type: 'Teams',
                options: this.teams.map(t => ({
                    id: t.id,
                    name: t.name,
                    isTeam: true
                }))
            },
            {
                type: 'Users',
                options: this.users.map(u => ({
                    id: u.userName,
                    name: u.userName,
                    isUser: true
                }))
            }
        ];

        // Only include Brand Portal Users option if users are available
        if (this.brandPortalUsers.length > 0) {
            defaultOptions.push({
                type: 'Brand Portal Users',
                options: this.brandPortalUsers.map(u => ({
                    id: `brandportal-${u.email}`,
                    name: u.name ? `${u.name} (${u.email})` : u.email,
                    email: u.email,
                    isBrandPortalUser: true,
                    programName: u.programName
                }))
            });
        }

        const activeAssigned = !this.activeAssignedTo ? null : (Array.isArray(this.activeAssignedTo) ? this.activeAssignedTo : [this.activeAssignedTo]);

        let patients: any[] = [];
        let prescribers: any[] = [];

        const isDuplicate = (array: any[], item: any) => array.some(a => a.id === item.id);

        if (activeAssigned?.find((a) => a.isPatient)) {
            patients = activeAssigned.filter((a) => a.isPatient);
        }

        if (activeAssigned?.find((a) => a.isPrescriber)) {
            prescribers = activeAssigned.filter((a) => a.isPrescriber);
        }

        if (this.patientOptions.length) {
            this.patientOptions.forEach(po => {
                const patient = {
                    id: `patient-${po.id}`,
                    name: Object.assign(new Patient(), po).toString(),
                    displayName: Object.assign(new Patient(), po).displayNamesForPerson(),
                    isPatient: true
                };
                if (!isDuplicate(patients, patient)) {
                    patients.push(patient);
                }
            });
        }

        if (this.prescriberOptions.length) {
            this.prescriberOptions.forEach(po => {
                const prescriber = {
                    id: `prescriber-${po.id}`,
                    name: Object.assign(new Prescriber(), po).toString(),
                    displayName: Object.assign(new Prescriber(), po).displayNamesForPerson(),
                    isPrescriber: true
                };
                if (!isDuplicate(prescribers, prescriber)) {
                    prescribers.push(prescriber);
                }
            });
        }

        if (this.value.patients) {
            this.value.patients.forEach(po => {
                const patient = {
                    id: `patient-${po.id}`,
                    name: Object.assign(new Patient(), po).toString(),
                    displayName: Object.assign(new Patient(), po).displayNamesForPerson(),
                    isPatient: true
                };
                if (!isDuplicate(patients, patient)) {
                    patients.push(patient);
                }
            });
        }

        if (this.value.prescriptions) {
            this.value.prescriptions.forEach(po => {
                if (!po.prescriber) return;
                const prescriber = {
                    id: `prescriber-${po.prescriber.id}`,
                    name: Object.assign(new Prescriber(), po.prescriber).toString(),
                    displayName: Object.assign(new Prescriber(), po.prescriber).displayNamesForPerson(),
                    isPrescriber: true
                };
                if (!isDuplicate(prescribers, prescriber)) {
                    prescribers.push(prescriber);
                }
            });
        }

        if (patients.length) {
            defaultOptions.push({
                type: 'Patients',
                options: patients
            })
        }

        if (prescribers.length) {
            defaultOptions.push({
                type: 'Prescribers',
                options: prescribers
            })
        }

        return defaultOptions;
    }

    get watchersOptions() {
        return [
            {
                type: 'Teams',
                options: this.teams.map(t => ({
                    id: t.id,
                    name: t.name,
                    isTeam: true
                }))
            },
            {
                type: 'Users',
                options: this.users.map(u => ({
                    id: u.userName,
                    name: u.userName
                }))
            }
        ]
    }

    get loadingAssignToOptions() {
        return this.loadingPatientOptions || this.loadingPrescriberOptions;
    }

    private assignToSearchCancelTokenSource: CancelTokenSource | null = null;

    private assignToSearch(searchQuery: string) {
        this.assignToSearchCancelTokenSource?.cancel("New search started");
        this.doDebouncedAssignToSearch(searchQuery, this);
    }

    private doDebouncedAssignToSearch = debounce(500, async (searchQuery, context) => {
        if (!searchQuery?.trim()) {
            context.patientOptions = [];
            context.prescriberOptions = [];
            return;
        }

        context.loadingPatientOptions = true;
        context.loadingPrescriberOptions = true;

        context.assignToSearchCancelTokenSource = Axios.CancelToken.source();

        const patientOptionsPromise = Axios.get<Patient[]>("/patient", {
            params: { searchTerm: searchQuery },
            cancelToken: context.assignToSearchCancelTokenSource.token
        });
        const prescriberOptionsPromise = Axios.get<Prescriber[]>("/prescriber", {
            params: { searchTerm: searchQuery },
            cancelToken: context.assignToSearchCancelTokenSource.token
        });

        try {
            context.patientOptions = (await patientOptionsPromise).data;
        } catch {

        }

        try {
            context.prescriberOptions = (await prescriberOptionsPromise).data;
        } catch {

        }

        context.loadingPatientOptions = false;
        context.loadingPrescriberOptions = false;
    });

    @Watch("activePatient")
    addPatient(patient?: Patient) {
        if (!patient) {
            patient = this.activePatient;
        }

        if (!patient?.id) return;
        this.activePatients.push(patient);
        this.activePatient = new Patient();

        this.$emit("input", {
            ...this.value,
            patients: this.activePatients
        });
    }

    assignToPrimaryPatient() {
        if (!this.primaryPatient) return;
        this.activeAssignedTo = {
            id: `patient-${this.primaryPatient.id}`,
            name: Object.assign(new Patient(), this.primaryPatient).toString(),
            displayName: Object.assign(new Patient(), this.primaryPatient).displayNamesForPerson(),
            isPatient: true
        };
    }

    assignToPrimaryPrescriber() {
        if (!this.primaryPrescriber) return;
        this.activeAssignedTo = {
            id: "prescriber-" + this.primaryPrescriber.id,
            name: Object.assign(new Prescriber(), this.primaryPrescriber).toString(),
            displayName: Object.assign(new Prescriber(), this.primaryPrescriber).displayNamesForPerson(),
            isPrescriber: true
        };
    }

    removePatient(item: Patient) {
        this.activePatients = this.activePatients.filter(p => p.id !== item.id);

        this.$emit("input", {
            ...this.value,
            patients: this.activePatients
        });
    }

    patientAddSearchResultFilter(patients: Patient[]) {
        return patients.filter(p => !this.activePatients.some(ap => ap.id === p.id));
    }

    @Watch("activePrescription")
    async addPrescription(prescription?: Prescription) {
        if (!prescription) {
            prescription = this.activePrescription;
        }

        if (!prescription?.rxNumber) return;

        if (!prescription.prescriber) {
            try {
                const prescriberResponse = await this.$http.get<Prescriber>(`/Prescriber/${prescription.prescriberID}`);
                prescription.prescriber = prescriberResponse.data;
            } catch {
                //ignore
            }
        }

        this.activePrescriptions.push(prescription);
        this.activePrescription = new Prescription();

        this.$emit("input", {
            ...this.value,
            prescriptions: this.activePrescriptions
        });
    }

    removePrescription(item: Prescription) {
        this.activePrescriptions = this.activePrescriptions.filter(p => !(p.storeID == item.storeID &&
            p.rxNumber == item.rxNumber &&
            p.rfNumber == item.rfNumber));

        this.$emit("input", {
            ...this.value,
            prescriptions: this.activePrescriptions
        });
    }

    prescriptionAddSearchResultFilter(prescriptions: Prescription[]) {
        return prescriptions.filter(p => !this.activePrescriptions.some(ap => ap.storeID == p.storeID &&
            ap.rxNumber == p.rxNumber &&
            ap.rfNumber == p.rfNumber));
    }

    prescriptionOptionLabel(prescription: Prescription) {
        const baseLabel = prescription.toString();
        const drugName = TextUtils.escapeHtml(prescription.drugName || '');
        const ndc = TextUtils.escapeHtml(prescription.ndc11 || '');
        const details = [drugName, ndc ? `NDC: ${ndc}` : ''].filter(Boolean).join(' | ');
        return details ? `${baseLabel} - ${details}` : baseLabel;
    }

    @Watch("activeStore")
    onActiveStoreChange() {
        this.$emit('input', {
            ...this.value,
            storeId: this.activeStore?.id
        })
    }

    @Watch("activeStoredPharmacy")
    onActiveStoredPharmacyChange() {
        this.$emit('input', {
            ...this.value,
            storedPharmacyId: this.activeStoredPharmacy?.id ?? null,
            storedPharmacy: this.activeStoredPharmacy?.id ? this.activeStoredPharmacy : null
        });
    }

    @Watch("value.startDate")
    onStartDateChange(_: Date, oldValue: Date) {
        if (this.value.isScheduled) {
            // Scheduled tasks: always maintain the day offset
            const dayDiff = moment.utc(this.value.dueDate).startOf('day')
                .diff(moment.utc(oldValue).startOf('day'), 'days');
            this.value.dueDate = moment.utc(this.value.startDate).add(dayDiff, "days").toDate();
        } else if (!this.value.isTemplate) {
            // Non-scheduled tasks: only update Due Date if Start Date moves past it
            const newStartDate = moment.utc(this.value.startDate).startOf('day');
            const currentDueDate = moment.utc(this.value.dueDate).startOf('day');

            if (newStartDate.isAfter(currentDueDate)) {
                // Start Date moved past Due Date - maintain the original day gap
                const dayDiff = currentDueDate.diff(moment.utc(oldValue).startOf('day'), 'days');
                this.value.dueDate = moment.utc(this.value.startDate).add(dayDiff, "days").toDate();
            }
        }
    }

    @Watch("activeAssignedTo")
    onActiveAssignedToChange() {
        const assignedToIsArray = Array.isArray(this.activeAssignedTo);

        let assignments = [];

        if (assignedToIsArray) {
            assignments = this.activeAssignedTo;
        } else if (this.activeAssignedTo) {
            assignments = [this.activeAssignedTo];
        }

        assignments = assignments.map((a: any) => ({
            teamId: a.isTeam ? a.id : undefined,
            userName: a.isUser ? a.name : undefined,
            patientId: a.isPatient ? a.id.replace("patient-", "") : undefined,
            prescriberId: a.isPrescriber ? a.id.replace("prescriber-", "") : undefined,
            brandPortalUserEmail: a.isBrandPortalUser ? (a.email || a.id.replace("brandportal-", "")) : undefined
        }));

        this.$emit('input', {
            ...this.value,
            assignments
        });
    }

    @Watch("activeWatchers")
    onActiveWatchersChange() {
        const watchers = (Array.isArray(this.activeWatchers) ? this.activeWatchers : [this.activeWatchers]).map(a => ({
            teamId: a.isTeam ? a.id : undefined,
            userName: !a.isTeam ? a.id : undefined
        }));

        this.$emit('input', {
            ...this.value,
            watchers
        });
    }

    assignToMe() {
        const userName = this.$user?.userName;
        if (!userName) return;

        const meAssignment = {
            id: userName,
            name: userName,
            isUser: true
        };

        // If multiple assignments allowed, add to existing assignments
        // Otherwise, replace with just "me"
        if (this.allowMultipleAssignments) {
            const existingAssignments = Array.isArray(this.activeAssignedTo) ? this.activeAssignedTo : [];
            // Check if already assigned to me
            if (existingAssignments.some((a: any) => a.id === userName)) return;
            this.activeAssignedTo = [...existingAssignments, meAssignment];
        } else {
            this.activeAssignedTo = meAssignment;
        }
    }

    addMeAsWatcher() {
        const userName = this.$user?.userName;
        if (!userName || this.activeWatchers.find((w: any) => w.id?.toLowerCase() == userName.toLowerCase())) return;
        this.activeWatchers.push({
            id: userName,
            name: userName
        });
    }

    updateActiveStatus(value: UserTaskStatus) {
        this.activeStatus = value;

        this.$emit('input', {
            ...this.value,
            status: value
        })
    }

    refreshNotes() {
        this.notesList?.loadNotes();
    }

    async savePendingNote() {
        // Check if there's a note form with unsaved content
        if (!this.notesForm || !this.value.id) {
            return;
        }

        // Access the note body from the NotesForm component
        const noteBody = this.notesForm.note?.body;

        // If there's unsaved content, save it
        if (noteBody && noteBody.trim()) {
            await this.notesForm.save();
        }
    }

    handleQuestionsUpdate(updatedQuestions: UserTaskQuestion[]) {
        const updatedValue = {
            ...this.value,
            completionQuestionSet: {
                ...(this.value.completionQuestionSet || {
                    questions: [],
                    entityType: EntityType.UserTask,
                    active: true
                }),
                questions: updatedQuestions
            }
        };
        this.$emit('input', updatedValue);
    }

    get eligibleDocumentPatientId(): number | null {
        const patients = this.activePatients;
        const prescriptions = this.activePrescriptions;

        // Collect all patient IDs from explicit patients and prescriptions
        const patientIds = new Set<number>();
        patients.forEach(p => { if (p.id) patientIds.add(p.id); });
        prescriptions.forEach(rx => { if (rx.patientID) patientIds.add(rx.patientID); });

        // Only eligible when exactly one identifiable patient across all sources
        if (patientIds.size === 1) {
            return [...patientIds][0];
        }

        return null;
    }

    get activePatientDocuments(): PatientDocument[] {
        return this.value.patientDocuments ?? [];
    }

    get documentsUniqueTags(): string[] {
        const tags: string[] = [];
        this.availableDocuments.forEach(pd => {
            pd.tags?.forEach(t => {
                const tag = t.toLowerCase().trim();
                if (tags.includes(tag)) return;
                tags.push(tag);
            });
        });
        tags.sort();
        return tags;
    }

    get filteredAvailableDocuments(): PatientDocument[] {
        let docs = this.availableDocuments.filter(pd => !pd.deleted);
        if (this.documentFilterTags.length) {
            docs = docs.filter(pd => pd.tags?.some(t => this.documentFilterTags.includes(t.toLowerCase().trim())));
        }
        // Exclude already linked documents
        const linkedIds = this.activePatientDocuments.map(d => d.id);
        docs = docs.filter(d => !linkedIds.includes(d.id));
        return docs;
    }

    async openDocumentSelector() {
        const patientId = this.eligibleDocumentPatientId;
        if (!patientId) return;

        this.loadingDocuments = true;
        this.documentFilterTags = [];
        this.showDocumentSelector = true;

        try {
            const response = await this.$http.get<PatientDocument[]>(`/patient/${patientId}/get-documents`);
            this.availableDocuments = response.data.map((d: any) => new PatientDocument(d));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        } finally {
            this.loadingDocuments = false;
        }
    }

    toggleDocumentFilterTag(tag: string) {
        const idx = this.documentFilterTags.indexOf(tag);
        if (idx >= 0) {
            this.documentFilterTags.splice(idx, 1);
        } else {
            this.documentFilterTags.push(tag);
        }
    }

    addDocuments() {
        const selected = this.filteredAvailableDocuments.filter(d => d.select);
        if (!selected.length) return;

        const current = this.activePatientDocuments;
        const merged = [...current, ...selected];

        this.$emit("input", {
            ...this.value,
            patientDocuments: merged
        });

        // Reset selection state
        this.availableDocuments.forEach(d => d.select = false);
        this.showDocumentSelector = false;
    }

    removeDocument(doc: PatientDocument) {
        const updated = this.activePatientDocuments.filter(d => d.id !== doc.id);
        this.$emit("input", {
            ...this.value,
            patientDocuments: updated
        });
    }

    async openDocument(doc: PatientDocument) {
        try {
            const patientId = this.eligibleDocumentPatientId;
            if (!patientId) return;

            const res = await this.$http.get(`patient/${patientId}/document/${doc.imageId}`, { responseType: "blob" });
            const objectUrl = URL.createObjectURL(res.data);
            window.open(objectUrl, '_blank');
        } catch (err) {
            console.error("Error opening document.", err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    getDocumentCountForTag(tag: string): number {
        return this.availableDocuments.filter(pd =>
            !pd.deleted && pd.tags?.some(t => t.toLowerCase().trim() === tag)
        ).length;
    }

    get getScheduleTypeOptions() {
        return [
            { text: "Day Interval", value: 0 },
            { text: "Days of Week", value: 1 },
            { text: "Days of Month", value: 2 }
        ]
    }

    handleDaySchedule(index: number) {
        let days = this.value.repeatSchedule?.split(",") ?? [];

        if (days?.includes(index.toString())) {
            days = days.filter(d => d !== index.toString());
        } else {
            days.push(index.toString());
        }

        days = days.filter(d => d);

        this.$emit('input', {
            ...this.value,
            repeatSchedule: days.join(",")
        });
    }

    handleMonthSchedule(day: number) {
        let days = this.value.repeatSchedule?.split(",") ?? [];

        if (days?.includes(day.toString())) {
            days = days.filter(d => d !== day.toString());
        } else {
            days.push(day.toString());
        }

        days = days.filter(d => d);

        days.sort((a, b) => Number(a) - Number(b));

        this.$emit('input', {
            ...this.value,
            repeatSchedule: days.join(",")
        });
    }

    get startDatePickerValue() {
        if (!this.value.startDate) return undefined;
        const utc = moment.utc(this.value.startDate);
        return moment([utc.year(), utc.month(), utc.date()]);
    }

    get dueDatePickerValue() {
        if (!this.value.dueDate) return undefined;
        if (this.value.dueAtTime) {
            return moment.utc(this.value.dueDate).local();
        }
        // Date-only: create a local moment with the same Y/M/D as the UTC date
        // so el-date-picker displays the correct calendar date regardless of timezone
        const utc = moment.utc(this.value.dueDate);
        return moment([utc.year(), utc.month(), utc.date()]);
    }

    get dueDateDaysAfter() {
        const dayDiff = moment.utc(this.value.dueDate).startOf('day')
            .diff(moment.utc(this.value.startDate).startOf('day'), 'days');

        return dayDiff ? dayDiff : undefined;
    }

    get expirationDatePickerValue() {
        if (!this.value.expiredOnDate) return undefined;
        const utc = moment.utc(this.value.expiredOnDate);
        return moment([utc.year(), utc.month(), utc.date()]);
    }

    get expirationDaysAfter() {
        if (!this.value.expiredOnDate) return undefined;

        const dayDiff = moment.utc(this.value.expiredOnDate).startOf('day')
            .diff(moment.utc(this.value.startDate).startOf('day'), 'days');

        return dayDiff ? dayDiff : undefined;
    }

    handleDueAfterCreation(days: string) {
        const time = this.value.dueAtTime
            ? moment.utc(this.value.dueDate).format("HH:mm:ss")
            : "00:00:00";
        this.handleDateTimeChange(moment.utc(this.value.startDate).add(days, 'days').toDate(), time);
    }

    handleExpiresAfterCreation(days?: string) {
        if (!days || days === "0") {
            this.value.expiredOnDate = undefined;
            return;
        }
        this.value.expiredOnDate = moment.utc(this.value.startDate).add(days, 'days').toDate();
    }

    async applyTemplate(task: UserTask) {
        try {
            task = (await this.$http.get<UserTask>(`/task/${task.id}`)).data;

            const dueDayDiff = moment.utc(task.dueDate).startOf('day')
                .diff(moment.utc(task.startDate).startOf('day'), 'days');
            const expiresDayDiff = task.expiredOnDate ? moment.utc(task.expiredOnDate).startOf('day')
                .diff(moment.utc(task.startDate).startOf('day'), 'days') : undefined;

            this.$emit('input', {
                ...task,
                patients: this.value.patients,
                prescriptions: this.value.prescriptions,
                startDate: moment.utc([moment().year(), moment().month(), moment().date()]),
                dueDate: moment.utc([moment().year(), moment().month(), moment().date()]).add(dueDayDiff, "days"),
                expiredOnDate: expiresDayDiff ? moment.utc([moment().year(), moment().month(), moment().date()]).add(expiresDayDiff, "days") : undefined,
                id: 0,
                isTemplate: this.value.isTemplate,
                isScheduled: this.value.isScheduled,
                createdByTemplateId: task.id,
                status: UserTaskStatus.new
            })
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    get filteredTemplates() {
        return this.templates.filter(t => {
            if (!t.limitToProgramIds?.length) return true;

            let shouldInclude = false;

            t.limitToProgramIds.forEach(lpid => {
                if (this.value.prescriptions?.some(p => p.programID == lpid)) {
                    shouldInclude = true;
                }
            });

            return shouldInclude;
        }).filter(t => {
            if (!t.limitToRxStatuses?.length) return true;

            let shouldInclude = false;

            t.limitToRxStatuses.forEach(lpid => {
                if (this.value.prescriptions?.some(p => p.status == lpid)) {
                    shouldInclude = true;
                }
            });

            return shouldInclude;
        })
            .filter(t => t.title.toLowerCase().includes(this.filterTemplates.toLowerCase()));
    }

    getPatientHtml(patient: Patient) {
        return `<a href='/processing/patient-dashboard/${patient.id}' target='_blank'>${patient.toString()}</a>`;
    }

    getPrescriptionHtml(prescription: Prescription) {
        const displayText = this.prescriptionOptionLabel(prescription);
        return `<a href='/processing/prescription/${prescription.storeID}/${prescription.rxNumber}/${prescription.rfNumber}' target='_blank'>${displayText}</a>`;
    }

    handleDateTimeChange(newDate: any, newTime: any) {
        if (!newDate) newDate = moment.utc(this.value.dueDate).toDate() ?? new Date();
        if (!newTime) newTime = moment.utc(this.value.dueDate).format("HH:mm:ss") ?? "12:00:00";

        const combinedDateTime = moment.utc(newDate).set({
            hour: parseInt(newTime.split(':')[0]),
            minute: parseInt(newTime.split(':')[1]),
            second: parseInt(newTime.split(':')[2])
        });

        this.$emit("input", { ...this.value, dueDate: combinedDateTime.toDate() });
    }

    handleExpiresDateChange(newDate: any) {
        if (!newDate) {
            this.$emit("input", { ...this.value, expiredOnDate: undefined });
            return;
        }
        const d = moment(newDate);
        const utcDate = moment.utc([d.year(), d.month(), d.date()]);
        this.$emit("input", { ...this.value, expiredOnDate: utcDate.toDate() });
    }

    handleStartDateChange(newDate: any) {
        if (!newDate) return;
        const d = moment(newDate);
        const utcDate = moment.utc([d.year(), d.month(), d.date()]);
        this.$emit("input", { ...this.value, startDate: utcDate.toDate() });
    }

    handleDateChange(newDate: any) {
        if (this.value.dueAtTime) {
            const d = moment(newDate);
            const utcDate = moment.utc([d.year(), d.month(), d.date()]);
            const time = moment.utc(this.value.dueDate).format("HH:mm:ss");
            this.handleDateTimeChange(utcDate.toDate(), time);
        } else {
            // Date-only: extract the local calendar date from the picker
            // and store it as midnight UTC to avoid timezone shifts
            const d = moment(newDate);
            const utcDate = moment.utc([d.year(), d.month(), d.date()]);
            this.$emit("input", { ...this.value, dueDate: utcDate.toDate() });
        }
    }

    handleTimeChange(newTime: any) {
        // Time picker emits local time — convert to UTC by building a local moment and converting
        const currentLocal = moment.utc(this.value.dueDate).local();
        const [h, m, s] = newTime.split(':').map(Number);
        const updatedLocal = currentLocal.clone().set({ hour: h, minute: m, second: s || 0 });
        this.$emit("input", { ...this.value, dueDate: updatedLocal.utc().toDate() });
    }

    get hideTabs() {
        if (this.value.isScheduled || this.value.isTemplate) return false;
        if (!this.value.id) return false;
        return !this.value.completionQuestionSet?.questions?.length && !this.value.completionFormImageId;
    }

    get showQuestionsTab() {
        if (this.value.isScheduled || this.value.isTemplate) return true;
        if (!this.value.id) return true;
        return this.value.completionQuestionSet?.questions.length;
    }

    resetActiveTab() {
        setTimeout(() => {
            this.activeTab = 0;
        }, 250);
    }

    get hasAdHocTaskPermission() {
        return this.$permissions.some(p => p.permissionCodeName === "Processing/Ad Hoc Tasks");
    }

    get hasTaskAdminPermission() {
        return this.$permissions.some(p => p.permissionCodeName === "Admin/Tasks");
    }

    get isRegularTask() {
        return !this.value.isScheduled && !this.value.isTemplate;
    }

    get isNewTask() {
        return (this.value.id ?? 0) === 0;
    }

    get canCreateAdHocTask() {
        return this.hasAdHocTaskPermission || this.hasTaskAdminPermission;
    }

    get disableAdHocFields() {
        return this.isRegularTask && !this.canCreateAdHocTask;
    }

    get outreachTokens(): string[] {
        return ["PatientFirstName", "PatientLastName", "PharmacyName", "PharmacyPhone", "PortalLink", "TaskCompletionLink"];
    }

    clampField(field: keyof UserTask, min: number): void {
        const val = this.value[field] as number | null | undefined;
        if (val === null || val === undefined || val === ("" as any)) {
            (this.value as any)[field] = null;
            return;
        }
        if (val < min) {
            (this.value as any)[field] = min;
        }
    }

    insertOutreachToken(token: string): void {
        const currentValue = this.value.outreachTextTemplate ?? "";
        const ref = this.$refs.outreachTextarea as any;
        const textArea = ref?.$el as HTMLTextAreaElement;

        const cursorStart = textArea?.selectionStart ?? currentValue.length;
        const cursorEnd = textArea?.selectionEnd ?? currentValue.length;
        const textToInsert = `{${token}}`;
        const newValue = currentValue.slice(0, cursorStart) + textToInsert + currentValue.slice(cursorEnd);

        this.$emit("input", { ...this.value, outreachTextTemplate: newValue });

        this.$nextTick(() => {
            const newPosition = cursorStart + textToInsert.length;
            if (textArea) {
                textArea.selectionStart = textArea.selectionEnd = newPosition;
                textArea.focus();
            }
        });
    }
}

</script>

<style>
.task-template-popover {
    width: 300px;
    max-width: 300px;
}

.task-template-popover .scroller {
    overflow-y: auto;
    max-height: 60vh;
}
</style>

<style scoped lang="scss">
.card {
    box-shadow: none;
}

.status.dropdown {
    .badge {
        display: block;
    }

    ::v-deep(.btn) {
        border: none !important;
        box-shadow: none;
        background: none;
        margin-top: 6px;
    }
}

textarea.form-control {
    max-height: 240px;
}

.schedule-month-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 6px;
}

::v-deep(.action-col) {
    width: 50px;
}

::v-deep(.b-form-timepicker) {
    height: 34px !important;
}

.btn-block + .collapse,
.btn-block + .collapsing {
    margin-top: 0 !important;
}

::v-deep(li:has(.multiselect__option--disabled)) {
    position: sticky;
    top: 0;
    z-index: 100;
}

.outreach-token {
    display: inline-block;
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    padding: 1px 5px;
    border-radius: 3px;
    cursor: pointer;
    font-size: 0.8rem;

    &:hover {
        background-color: #e9ecef;
    }
}
</style>
